import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import { UserRole } from "../../../../app/userSlice";
import { scrollToSection, useScrollToSection } from "../../../../app/useScrollToSection";
import { AvatarWithName } from "../../../../components/Avatar";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import { usePromptReportQuery } from "./promptReportApiSlice";
import { EssayPromptScoreTable } from "./EssayPromptScoreTable";
import { CrPromptScoreTable } from "./CrPromptScoreTable";
import { PromptScoreChart } from "./PromptScoreChart";

export const STUDENT_PROMPT_ANALYSIS = "prompt-analysis";
export const STUDENT_CR_PROMPT_ANALYSIS = "cr-prompt-analysis";

export default function StudentPromptReport() {
	const [queryParams] = useSearchParams();
	const writerIdQueryParam = getParamAsInt(queryParams, "writerId");
	const courseIdQueryParam = getParamAsInt(queryParams, "courseId");
	const sectionQueryParam = queryParams.get("section");

	const [view, setView] = useState<string>("legacy");
	const { aspUrl } = useAppSelector((state) => state.site);
	const { userId, course, role } = useAppSelector((state) => state.user);

	const writerId = writerIdQueryParam ?? userId!;
	const courseId = courseIdQueryParam ?? course!.id;

	const result = usePromptReportQuery({ writerId, courseId });
	const data = result.data;

	useScrollToSection();
	sectionQueryParam && scrollToSection(sectionQueryParam);

	const Heading = () => <h1>{role !== UserRole.Writer ? "Student " : ""}Prompt Report</h1>;
	const Subheading = () =>
		role === UserRole.Writer || !data ? (
			""
		) : (
			<h2 className="mb-4 font-medium">
				<AvatarWithName
					userId={data.writerId}
					name={data.writerName}
					initials={data.writerInitials ?? "?"}
					hasImage={data.writerHasImage}
				/>
			</h2>
		);

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<Subheading />
					<Heading />
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "react", label: "React" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "legacy" && (
				<div>
					<section id={STUDENT_PROMPT_ANALYSIS} className="pt-4">
						<h2>Prompt Analysis</h2>
						<p className="mt-4">
							The report below provides a list of prompts and the date of your most
							recent draft submission for each prompt. It also displays the number of
							drafts and the scores for the most recent draft for each prompt. To view
							the most recent Score Report for a prompt, click on the date.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex mt-4 place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadPromptUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.prompts.length == 0}
									/>
								</div>
								<EssayPromptScoreTable className="mt-4" scores={data.prompts} />
								<PromptScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									scores={data.prompts}
									maxValue={30}
								/>
							</>
						)}
					</section>
					<section id={STUDENT_CR_PROMPT_ANALYSIS} className="pt-8">
						<h2>Constructed Response Prompt Analysis</h2>
						<p className="mt-4">
							The report below provides a list of constructed response prompts and the
							date of the most recent draft submission for each prompt. It also
							displays the number of drafts, and the score for the most recent draft
							for each prompt. To view the most recent Score Report for a prompt,
							click on the date.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex mt-4 place-content-end w-full">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponsePromptUsageFilteredByWriter?courseId=${courseId}&writerId=${writerId}`}
										disabled={data.constructedResponsePrompts.length == 0}
									/>
								</div>
								<CrPromptScoreTable
									className="mt-4"
									scores={data.constructedResponsePrompts}
								/>
								<PromptScoreChart
									className="mt-8 p-4 border-2 border-gray-200"
									scores={data.constructedResponsePrompts}
									maxValue={3}
								/>
							</>
						)}
					</section>
				</div>
			)}
			{view == "raw" && <RtkqResultViewer heading="Prompt Report Data" result={result} />}
		</section>
	);
}
